import { useEffect, useState } from 'react';
import {sides} from "../../../const/sides";
import { BaseSides, FlagImage, LogoImages, TeamImage, Image } from '../../types/builder';
import {shareDataConverter} from "../../utils/ShareDataConverter";
import omit from "lodash/omit";

const imagesConfig = {
  maxLogos: {
    [sides.BACK]: 4,
    [sides.FRONT]: 4,
    [sides.LEFT]: 2,
    [sides.RIGHT]: 2,
    [sides.LEFT_LONG]: 2,
    [sides.RIGHT_LONG]: 2
  }
};

const defaultImage = {
  id: null,
  image: null,
  public_url: null,
  loading: false,
  layersMeta: {}
}

const defaultTeamImage = {
  id: null,
  image: null,
  public_url: null,
  loading: false,
  [sides.FRONT]: false,
  [sides.BACK]: false,
  [sides.LEFT]: false,
  [sides.RIGHT]: false,
  [sides.LEFT_LONG]:false,
  [sides.RIGHT_LONG]:false,
  layersMeta: {
    front: null,
    back: null,
    left: null,
    right: null,
    left_long: null,
    right_long: null
  },
}

const defaultSponsorImages = {
  [sides.BACK]: Array(imagesConfig.maxLogos[sides.BACK]).fill(null),
  [sides.FRONT]: Array(imagesConfig.maxLogos[sides.FRONT]).fill(null),
  [sides.LEFT]: Array(imagesConfig.maxLogos[sides.LEFT]).fill(null),
  [sides.RIGHT]: Array(imagesConfig.maxLogos[sides.RIGHT]).fill(null),
  [sides.LEFT_LONG]: Array(imagesConfig.maxLogos[sides.LEFT_LONG]).fill(null),
  [sides.RIGHT_LONG]: Array(imagesConfig.maxLogos[sides.RIGHT_LONG]).fill(null),
}

const defaultSponsorFlags = {
  [sides.LEFT]: {
    enabled: false,
    id: null
  },
  [sides.RIGHT]: {
    enabled: false,
    id: null
  }
}

const useImageFiles = function(controller, loadedData, options, isReady, logoTabs, currenSizeMouseMat) {
  // Initialize state vars
  const [activeUploadsCounter, setActiveUploadsCounter] = useState(0);
  const [sponsorImages, setSponsorImages] = useState<LogoImages>(loadedData?.imagesData?.sponsorImages ? {...loadedData?.imagesData?.sponsorImages, ...defaultSponsorImages} : defaultSponsorImages);
  const [teamImage, setTeamImage] = useState<TeamImage>(loadedData?.imagesData?.teamImage ? {...loadedData?.imagesData?.teamImage, defaultTeamImage} : defaultTeamImage);
  const [image, setImage] = useState<Image>(loadedData?.imagesData?.image ? {...loadedData?.imagesData?.image, defaultImage} : defaultImage);

  const [sponsorFlag, setSponsorFlag] = useState<FlagImage>(loadedData?.imagesData?.sponsorFlag ?? defaultSponsorFlags);

  // Load meta image
  useEffect(() => {
    if (controller.current && isReady) {
      if(loadedData){
        loadedData = shareDataConverter(loadedData, options);

        loadedData?.imagesData?.sponsorImages && setSponsorImages(loadedData?.imagesData?.sponsorImages);
        loadedData?.imagesData?.teamImage && setTeamImage(loadedData?.imagesData?.teamImage);
        loadedData?.imagesData?.image && setImage(loadedData?.imagesData?.image);
      }

      let sidesList = [];
      for(let tab in logoTabs.teamLogo){
        if(logoTabs.teamLogo[tab]){
          if([sides.LEFT, sides.RIGHT].includes(tab)){
            if(options?.sleeve) sidesList.push(tab);
            sidesList.push(tab+"_long")
          }else sidesList.push(tab);
        }
      }

      sidesList.forEach((side) => {
        if (loadedData?.imagesData?.teamImage) {
          const { image, layersMeta} = loadedData?.imagesData?.teamImage;
          let positionMeta = layersMeta?.[side]?.position ?? options?.positions?.image?.[side];
          if (Array.isArray(positionMeta)) {
            const position = Object.fromEntries(positionMeta.map((o, i) => [i ? 'y' : 'x', o]));
            const [, , w, h] = positionMeta;
            controller.current.allSidesImages[side] = controller?.current?.placeImage(
                {image, meta: layersMeta[side], position, layerId: `teamlogo_${side}`, visible: loadedData?.imagesData?.teamImage[side], side, w, h});
          }
        }

        if (loadedData?.imagesData?.sponsorImages) {
          loadedData?.imagesData?.sponsorImages[side].forEach(sponsorImage => {
            if (sponsorImage) {
              const { image, layerMeta, public_url, id } = sponsorImage;
              let positionMeta = layerMeta?.position ?? options?.positions?.image?.[side];
              if (!Array.isArray(positionMeta)) return;
              const [x, y, w, h] = positionMeta;
              public_url && controller?.current?.placeImage({ image, meta: layerMeta, position:{x, y}, layerId:id, visible: true, side, w, h });
            }
          })
        }

        if(loadedData?.imagesData?.sponsorFlag){
          Object.keys(loadedData?.imagesData?.sponsorFlag).forEach(side => {
            updateFlag(side, loadedData?.imagesData?.sponsorFlag[side])
          })
        }
      });

      // For mouse mat
      // Do not delete this code
      if(loadedData?.imagesData?.image){
        const { image, layersMeta, public_url, id } = loadedData?.imagesData?.image;
        let positionMeta = layersMeta?.metaData?.position ?? options?.positions?.single_image;
        if (Array.isArray(positionMeta)) {
          const [x, y, w, h] = positionMeta;
          public_url && controller?.current?.placeImageMouseMat({ image, meta: layersMeta?.metaData, position:{x,y}, layerId:id, visible: true, w, h });
        }
      }

      if(logoTabs.additionalImage?.image_1 || logoTabs?.image?.image){
        if (loadedData?.imagesData?.sponsorImages) {
          loadedData?.imagesData?.sponsorImages['front'].forEach(sponsorImage => {
            if (sponsorImage) {
              const { image, layerMeta, public_url, id } = sponsorImage;
              let positionMeta = layerMeta?.position ?? options?.positions?.image?.['front'];
              let side = 'front';
              if (!Array.isArray(positionMeta)) return;
              const [x, y, w, h] = positionMeta;
              public_url && controller?.current?.placeImage({ image, meta: layerMeta, position:{x, y}, layerId:id, visible: true, side, w, h });
            }
          })
        }
      }
      // End

      controller.current.events.on('update_model_image', updateMetaImage);
    }
  }, [controller, isReady]);

  // Functions
  const updateMetaImage = ({ layer, id }) => {
    const metaData = layer.toJSON();
    if(layer.checkMouse){
      const imgSize = layer.getImageSize();
      metaData.posX = layer.sizeBoxMask_XTop;
      metaData.posY = layer.sizeBoxMask_YLeft;
      metaData.sizeImg = imgSize;
    }
    Object.values(sides).forEach((side) => {
      // Update sponsor images layer
      setSponsorImages(sponsorImagesPrev => {
        const sponsorImageIndex = sponsorImagesPrev[side].findIndex(el => {
          return el?.id === id
        });
        if (sponsorImageIndex === -1) return sponsorImagesPrev;
        return { ...sponsorImagesPrev, [side]: sponsorImagesPrev[side].map((el, i) => i === sponsorImageIndex ? { ...el, layerMeta: metaData } : el) };
      });

      // Update team image layers
      setTeamImage(teamImagePrev => {
        if (!teamImagePrev?.image || id !== `teamlogo_${side}`) return teamImagePrev;
        return { ...teamImagePrev, layersMeta: { ...teamImagePrev.layersMeta, [side]: metaData }  };
      });

      // Update Single image
      setImage(singleImage => {
        if(id === "single_image") return { ...singleImage, layersMeta: { metaData } };
        return singleImage;
      })
    });

  };

  const updateSponsorImage = (side, index, image: HTMLImageElement | null, public_url: string) => {

    const updateImage = (side) => {
      setTimeout(() => {
        let layerId = `image_${side}_${Math.random() * 1000}`;
        const updateImage = (prevEl) => {
          if (image) {

            return { ...prevEl, image, id:layerId, layerMeta: null, loading: true };
          } else if (public_url) {
            return { ...prevEl,  public_url, loading: false };
          } else return null;
        }

        setSponsorImages(prevState => {
          if(image && prevState[side][index]) {
            controller?.current?.removeLayer(prevState[side][index].id);
          }
          return { ...prevState, [side]: prevState[side].map((el, i) => i === index ? updateImage(el) : el) }
        });

        if (image) {
          let positionMeta = options?.positions?.image?.[side];
          if (Array.isArray(positionMeta)) {
            const [x, y, w, h] = positionMeta;
            let mouseMatSize = currenSizeMouseMat
            controller?.current?.placeImage({ image, layerId, position:{x, y}, visible: true, side, w, h, mouseMatSize });
          }
        } else if(!public_url) {
          controller?.current?.removeLayer(sponsorImages[side][index].id)
        }
      },100)
    }

    if([sides.LEFT, sides.RIGHT].includes(side)){
      if(options?.sleeve) updateImage(side);
      updateImage(`${side}_long`);
    }else updateImage(side);
  };

  const updateFlag = (side, data) => {
    setSponsorFlag(prevState => ({...prevState, [side]: {...prevState[side], ...data}}));

    const updateImage = (flagSide) => {
      const flagPos = options?.positions?.flags?.[flagSide];
      if (!Array.isArray(flagPos)) return;
      const [x, y, w, h] = flagPos;
      controller?.current?.updateFlag(flagSide, data.id, data.enabled, {x, y}, {w,h});
    }

    if([sides.LEFT, sides.RIGHT].includes(side)){
      if(options?.sleeve) updateImage(side);
      updateImage(`${side}_long`);
    }
  }

  const updateTeamImage = (image: HTMLImageElement, public_url: string) => {
    if (public_url) {
      setTeamImage(prevState => ( prevState.image ? { ...prevState, public_url, loading: false } : prevState));
    } else if (image) {
      let teamImageSides = {};

      for(let side in logoTabs?.teamLogo){
        if(logoTabs?.teamLogo?.hasOwnProperty(side)) {
          if(side === sides.LEFT || side === sides.RIGHT)  {
            teamImageSides[`${side}_long`] = Boolean(logoTabs?.teamLogo[side]);
            teamImageSides[side] = Boolean(logoTabs?.teamLogo[side]) && options?.sleeve;
          }
          else teamImageSides[side] = Boolean(logoTabs?.teamLogo[side]);
        }
      }

      setTeamImage(prevState => ({ ...prevState, image, id: image.alt ?? image.id ?? image.src, loading: true, ...teamImageSides }));
      let tabs = {...logoTabs?.teamLogo};

      if(options?.sleeve) {
        tabs[sides.LEFT] && (tabs[sides.LEFT_LONG] = true);
        tabs[sides.RIGHT] && (tabs[sides.RIGHT_LONG] = true);
      }
      else {
        if(tabs[sides.LEFT]){
          tabs[sides.LEFT_LONG] = true;
          tabs = omit(tabs, [sides.LEFT]);
        }
        if(tabs[sides.RIGHT]){
          tabs[sides.RIGHT_LONG] = true;
          tabs = omit(tabs, [sides.RIGHT]);
        }
      }
      setTimeout(() => controller?.current?.placeGroupImage({ image, options, visible: true, tabs }), 50);
    }else{
      setTeamImage(prevState => ({ ...prevState, ...defaultTeamImage }));
      setTimeout(() => controller?.current?.removeGroupImages(),100);
    }
  };

  const updateImage = (image: HTMLImageElement, public_url: string) => {
    if (public_url) {
      setImage(prevState => (prevState.image ? {...prevState, public_url, loading: false} : prevState));
    }else if(image){
      setImage(prevState => ({ ...prevState, image, id: image.alt ?? image.id ?? image.src, loading: true, public_url }));
      const positionSingle = options?.positions?.single_image;
      if (Array.isArray(positionSingle)) {
        const[x, y, w, h] = positionSingle;
        let mouseMatSize = currenSizeMouseMat;
        setTimeout(() => controller?.current?.placeImageMouseMat({ image, position:{x, y}, visible: true, w,h, mouseMatSize }), 50);
      }
    }else{
      setImage(prevState => ({ ...prevState, ...defaultImage }));
      setTimeout(() => controller?.current?.removeImage(),100);
    }
  };

  const toggleSideTeamImage = (side: BaseSides, visible) => {

    const toggleImage = (imageSide) => {
      controller?.current?.setGroupImageVisible(imageSide, visible);
      setTeamImage(prevState => ({ ...prevState, [imageSide]: !prevState[imageSide] }));
    }

    if([sides.LEFT, sides.RIGHT].includes(side)){
      options?.sleeve && toggleImage(side);
      toggleImage(`${side}_long`);
    }else toggleImage(side);

  };

  return {
    sponsorImages,
    updateSponsorImage,
    sponsorFlag,
    updateFlag,

    teamImage,
    toggleSideTeamImage,
    updateTeamImage,

    image,
    updateImage,

    activeUploadsCounter,
    setActiveUploadsCounter
  }
}

export default useImageFiles;